import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { memo, useCallback, useMemo, useState } from 'react'
import {
  IoChevronDown,
  IoChevronUp,
  IoClose,
  IoFunnelOutline,
  IoSearchOutline,
} from 'react-icons/io5'

const FIELD_LABELS = {
  competence_tags: 'Competências',
  ability_tags: 'Habilidades',
  subject_tags: 'Disciplinas',
  content_tags: 'Conteúdos',
} as const

const DIFFICULTY_OPTIONS = [
  { value: '', label: 'Todas as dificuldades' },
  { value: 'easy', label: 'Fácil' },
  { value: 'intermediary', label: 'Médio' },
  { value: 'hard', label: 'Difícil' },
] as const

const TYPE_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'multiple choice', label: 'Múltipla escolha' },
  { value: 'discursive', label: 'Discursiva' },
] as const

export interface QuestionCardFilterProps {
  filters: {
    type: string
    query: string
    difficulty: string
    competence_tags: string[]
    ability_tags: string[]
    subject_tags: string[]
    content_tags: string[]
  }
  tagOptions: {
    competence_tags: string[]
    ability_tags: string[]
    subject_tags: string[]
    content_tags: string[]
  }
  onFilterChange: (field: string, value: string | string[]) => void
}

export const QuestionCardFilter = memo<QuestionCardFilterProps>(
  ({ filters, tagOptions, onFilterChange }) => {
    const theme = useTheme()
    const [showAdvanced, setShowAdvanced] = useState(false)

    const handleToggleAdvanced = useCallback(() => {
      setShowAdvanced((prev) => !prev)
    }, [])

    const handleClearFilters = useCallback(() => {
      onFilterChange('query', '')
      onFilterChange('type', '')
      onFilterChange('difficulty', '')
      onFilterChange('competence_tags', [])
      onFilterChange('ability_tags', [])
      onFilterChange('subject_tags', [])
      onFilterChange('content_tags', [])
    }, [onFilterChange])

    const handleRemoveFilter = useCallback(
      (field: string, value?: string) => {
        if (field === 'query' || field === 'type' || field === 'difficulty') {
          onFilterChange(field, '')
        } else if (value) {
          const currentValues = filters[field as keyof typeof filters] as string[]
          onFilterChange(
            field,
            currentValues.filter((v) => v !== value),
          )
        }
      },
      [filters, onFilterChange],
    )

    const activeFilters = useMemo(() => {
      const active = []

      if (filters.query) {
        active.push({
          type: 'query',
          label: `Busca: "${filters.query}"`,
          value: filters.query,
        })
      }

      if (filters.type) {
        const typeLabel = TYPE_OPTIONS.find((opt) => opt.value === filters.type)?.label
        active.push({ type: 'type', label: `Tipo: ${typeLabel}`, value: filters.type })
      }

      if (filters.difficulty) {
        const diffLabel = DIFFICULTY_OPTIONS.find(
          (opt) => opt.value === filters.difficulty,
        )?.label
        active.push({
          type: 'difficulty',
          label: `Dificuldade: ${diffLabel}`,
          value: filters.difficulty,
        })
      }

      Object.entries(FIELD_LABELS).forEach(([key, label]) => {
        const values = filters[key as keyof typeof filters] as string[]
        values.forEach((value) => {
          active.push({ type: key, label: `${label}: ${value}`, value })
        })
      })

      return active
    }, [filters])

    const hasActiveFilters = useMemo(() => activeFilters.length > 0, [activeFilters])

    return (
      <Box data-qa="question-card-filter">
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Buscar por questão, descrição ou autor..."
            value={filters.query || ''}
            onChange={(e) => onFilterChange('query', e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IoSearchOutline color={theme.palette.action.active} />
                  </InputAdornment>
                ),
                endAdornment: filters.query && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => onFilterChange('query', '')}
                      sx={{ color: 'text.secondary' }}>
                      <IoClose />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo de questão</InputLabel>
              <Select
                value={filters.type}
                label="Tipo de questão"
                onChange={(e) => onFilterChange('type', e.target.value)}
                sx={{ borderRadius: 2 }}>
                {TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Dificuldade</InputLabel>
              <Select
                value={filters.difficulty}
                label="Dificuldade"
                onChange={(e) => onFilterChange('difficulty', e.target.value)}
                sx={{ borderRadius: 2 }}>
                {DIFFICULTY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Button
            variant="text"
            onClick={handleToggleAdvanced}
            startIcon={<IoFunnelOutline />}
            endIcon={showAdvanced ? <IoChevronUp /> : <IoChevronDown />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
            }}>
            Filtros avançados
          </Button>
        </Box>

        <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
          <Box
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Filtrar por tags
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(FIELD_LABELS).map(([key, label]) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={tagOptions[key as keyof typeof tagOptions]}
                    value={filters[key as keyof typeof filters] as string[]}
                    onChange={(_, value) => onFilterChange(key, value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={label}
                        placeholder={`Selecionar ${label.toLowerCase()}`}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Collapse>

        {hasActiveFilters && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main + '08',
              border: `1px solid ${theme.palette.primary.light}`,
            }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}>
              <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                Filtros ativos ({activeFilters.length})
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={handleClearFilters}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  color: 'text.secondary',
                }}>
                Limpar todos
              </Button>
            </Stack>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {activeFilters.map((filter, index) => (
                <Chip
                  key={`${filter.type}-${filter.value}-${index}`}
                  label={filter.label}
                  size="small"
                  variant="filled"
                  color="primary"
                  onDelete={() => handleRemoveFilter(filter.type, filter.value)}
                  deleteIcon={<IoClose />}
                  sx={{
                    '& .MuiChip-deleteIcon': {
                      fontSize: '1rem',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    )
  },
)

QuestionCardFilter.displayName = 'QuestionCardFilter'
